import { apiClient } from '../../../../api/axios';
import type { ApiResponse, PaginatedResponse, Submission, Category, GeoPoliticalZone, DeskItem } from '../common/types';
import type { GetReviewerAssignmentPayload, ReviewedManuscriptForReReviewItem } from '../reviewers/types';
import type {
    CommunicateSubmissionStatusToAuthorPayload,
    MoveSubmissionToAuthorPayload,
    SearchSubmissionPayload,
    GetSubmissionsPayload,
    GetAllAdminSubmissionsPayload,
    SubmissionWithReviewer,
    SubmissionReviewProgress,
    GetSubmissionsByDeskPayload,
    MoveSubmissionPayload,
    UploadedVersion,
} from './types';

export const submissionsByDeskService = {
    getSubmissionsByDesk: async (
        payload: GetSubmissionsByDeskPayload
    ): Promise<ApiResponse<PaginatedResponse<Submission>>> => {
        const response = await apiClient.post('/Submissions/GetSubmissionByDesk', {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            sortBy: payload.sortBy || '',
            sortDescending: payload.sortDescending ?? true,
            search: payload.search || '',
            categoryId: payload.categoryId ?? 0,
        });
        return response.data;
    },

    getDeskList: async (): Promise<ApiResponse<DeskItem[]>> => {
        const response = await apiClient.get('/Roles/GetDeskList');
        return response.data;
    },

    moveSubmission: async (
        payload: MoveSubmissionPayload
    ): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/Submissions/MoveSubmission', payload);
        return response.data;
    },
};

export const submissionService = {
    communicateSubmissionStatusToAuthor: async (payload: CommunicateSubmissionStatusToAuthorPayload): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/Submissions/CommunicateSubmissionStatusToAuthor', payload);
        return response.data;
    },

    moveSubmissionToAuthor: async (payload: MoveSubmissionToAuthorPayload): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/Submissions/MoveSubmissionToAuthor', payload);
        return response.data;
    },

    searchSubmissions: async (payload: SearchSubmissionPayload): Promise<ApiResponse<PaginatedResponse<Submission>>> => {
        const response = await apiClient.post('/Submissions/SearchForSubmission', payload);
        return response.data;
    },

    getAllSubmissions: async (payload: GetSubmissionsPayload): Promise<ApiResponse<PaginatedResponse<Submission>>> => {
        const requestPayload = {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            sortBy: payload.sortBy || '',
            sortDescending: payload.sortDescending ?? true,
            search: payload.search || '',
            categoryId: payload.categoryId || 0,
            cycleId: payload.cycleId || 0,
            authorId: payload.authorId || '00000000-0000-0000-0000-000000000000',
            submittedFrom: payload.submittedFrom || null,
            submittedTo: payload.submittedTo || null,
            applicationType: payload.applicationType || 0,
            geoPoliticalZoneId: payload.geoPoliticalZoneId || 0
        };
        const response = await apiClient.post('/Submissions/GetSubmissions', requestPayload);
        return response.data;
    },

    getAllAdminSubmissions: async (payload: GetAllAdminSubmissionsPayload): Promise<ApiResponse<PaginatedResponse<Submission>>> => {
        // 0 means "All" for every filter — backend now treats 0 as no filter.
        const response = await apiClient.post('/Submissions/GetAllSubmissions', {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            sortBy: payload.sortBy || '',
            sortDescending: payload.sortDescending ?? true,
            search: payload.search || '',
            categoryId: payload.categoryId ?? 0,
            submissionStatus: payload.submissionStatus ?? 0,
            currentStage: payload.currentStage ?? 0,
        });
        return response.data;
    },

    getSubmissionById: async (submissionId: string): Promise<ApiResponse<Submission>> => {
        const response = await apiClient.get(`/Submissions/GetSubmissionById?submissionId=${submissionId}`);
        return response.data;
    },

    getAllCategories: async (status?: boolean): Promise<ApiResponse<Category[]>> => {
        const url = status !== undefined ? `/Category/GetAllCategory?status=${status}` : '/Category/GetAllCategory';
        const response = await apiClient.get(url);
        return response.data;
    },

    getGeoPoliticalZones: async (): Promise<ApiResponse<GeoPoliticalZone[]>> => {
        const response = await apiClient.get('/Commons/GetGeoPoliticalZones');
        return response.data;
    },

    automaticAssignment: async (payload: { cycleId: number; applicationType: number; categoryId: number; reviewersPerSubmission: number }): Promise<ApiResponse> => {
        const response = await apiClient.post('/ReviewAssignments/assign', payload);
        return response.data;
    },

    getReviewersByCategory: async (categoryId: number): Promise<ApiResponse<{ id: string; name: string }[]>> => {
        const response = await apiClient.get(`/ReviewCategory/GetReviewerCategoryDropdown?categoryId=${categoryId}`);
        return response.data;
    },

    manualAssignment: async (payload: { submissionId: string; reviewerIds: string[]; ignoreCategory?: boolean; deadLineDate: string }): Promise<ApiResponse> => {
        const requestPayload = {
            ...payload,
            ignoreCategory: payload.ignoreCategory ?? true
        };
        const response = await apiClient.post('/ReviewAssignments/manual-assign', requestPayload);
        return response.data;
    },

    reassignReviewer: async (payload: { submissionId: string; oldReviewerId: string; newReviewerId: string; reason: string }): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/ReviewAssignments/ReassignReviewer', payload);
        return response.data;
    },

    allowReAssignment: async (submissionId: string): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post(`/ReviewAssignments/AllowReAssignment?submissionId=${encodeURIComponent(submissionId)}`);
        return response.data;
    },

    requestReReview: async (payload: { reviewAssignmentId: string; comment: string; deadLineDate: string }): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/ReviewAssignments/RequestReReview', payload);
        return response.data;
    },

    getReviewedManuscriptForReReviewByAdmin: async (payload: GetReviewerAssignmentPayload): Promise<ApiResponse<PaginatedResponse<ReviewedManuscriptForReReviewItem>>> => {
        const response = await apiClient.post('/ReviewAssignments/GetReviewedManuscriptForReReviewByAdmin', payload);
        return response.data;
    },

    getSubmissionsWithReviewers: async (payload: GetSubmissionsPayload): Promise<ApiResponse<PaginatedResponse<SubmissionWithReviewer>>> => {
        const requestPayload = {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            sortBy: payload.sortBy || '',
            sortDescending: payload.sortDescending ?? true,
            search: payload.search || '',
            categoryId: payload.categoryId !== undefined ? payload.categoryId : 0,
            cycleId: payload.cycleId !== undefined ? payload.cycleId : 0,
            authorId: payload.authorId || '00000000-0000-0000-0000-000000000000',
            submittedFrom: payload.submittedFrom || null,
            submittedTo: payload.submittedTo || null,
            applicationType: payload.applicationType ?? 0,
            geoPoliticalZoneId: payload.geoPoliticalZoneId ?? 0
        };
        const response = await apiClient.post('/Submissions/submissions-with-reviewers', requestPayload);
        return response.data;
    },

    getReviewAssignmentReviewProgress: async (payload: {
        pageNumber: number;
        pageSize: number;
        sortBy: string;
        sortDescending: boolean;
        search: string;
        categoryId: number;
        applicationType: number;
        cycleId: number;
    }): Promise<ApiResponse<PaginatedResponse<SubmissionReviewProgress>>> => {
        const response = await apiClient.post('/Reviews/GetReviewAssignmentReviewProgress', payload);
        return response.data;
    }
};

export const submissionVersionService = {
    getUploadedVersionsBySubmissionId: async (
        submissionId: string
    ): Promise<ApiResponse<UploadedVersion[]>> => {
        const response = await apiClient.get(
            `/Submissions/GetUploadedVersionsBySubmissionId?submissionId=${submissionId}`
        );
        return response.data;
    },
};
